/**
 * Which CGT-relevant assets are missing the inputs needed to estimate capital gains tax properly.
 *
 * The CGT engine can always produce a number — where the cost base is blank it assumes 25% of current
 * value, and where the acquisition month is blank it assumes the asset has been held more than 12 months
 * (so the 50% discount applies). Both assumptions are reasonable defaults and both can be materially
 * wrong, so rather than either forcing clients to supply data they may not have or letting the guess pass
 * silently, we surface the gap.
 *
 * NO VALUE THRESHOLD. An earlier version only flagged assets worth $50,000+, on the theory that small
 * holdings were not worth prompting about. In practice that meant two assets missing the same field were
 * treated differently with nothing on screen to explain why, which reads as a bug — and the assumed
 * inputs distort a small holding's CGT just as much in percentage terms. Every CGT-relevant asset with a
 * gap is now flagged, which is only tolerable because the prompt is quiet helper text rather than a
 * warning; if it is ever restyled as an alarm, revisit that.
 *
 * Used by the fact-find to prompt inline, and by the SOA to disclose assumptions behind an asset the
 * advice actually sells. The CGT engine separately attaches its own warning to any estimate that rests
 * on the acquisition-date assumption (see disposalCgt.ts).
 */
import { assetTypeHasCgt } from './constants/assetTypes.ts';
import { isPreCgtAsset, resolveAcquisitionDate } from './capitalGainsTax.ts';
import type { FactFindAsset, FactFindAssetMap } from './factFindTypes.ts';

export interface CgtDataGap {
    assetType: string;
    assetId: string | number | undefined;
    description: string;
    value: number;
    /** No cost base recorded — the engine will assume 25% of current value. */
    missingCostBase: boolean;
    /** No acquisition month recorded — the engine will assume the asset was held over 12 months. */
    missingAcquiredOn: boolean;
}

/**
 * The CGT data gap on ONE asset, or null when there is nothing to raise.
 * Pre-1985 assets are exempt, so neither input affects their (nil) CGT and neither is requested.
 */
export function getAssetCgtDataGap(asset: FactFindAsset | null | undefined, assetType: string): CgtDataGap | null {
    if (!asset || !assetTypeHasCgt(assetType)) return null;
    if (isPreCgtAsset(asset)) return null;

    const value = Number(asset.value) || 0;
    const missingCostBase = !(Number(asset.costBase) > 0);
    const missingAcquiredOn = !resolveAcquisitionDate(asset);
    if (!missingCostBase && !missingAcquiredOn) return null;

    return {
        assetType,
        assetId: asset.id,
        description: (asset.description || '').trim(),
        value,
        missingCostBase,
        missingAcquiredOn,
    };
}

/** Every CGT data gap across a fact-find's assets. */
export function findCgtDataGaps(assets: FactFindAssetMap | null | undefined): CgtDataGap[] {
    if (!assets) return [];
    const gaps: CgtDataGap[] = [];
    Object.entries(assets).forEach(([assetType, list]) => {
        (list || []).forEach((asset) => {
            const gap = getAssetCgtDataGap(asset, assetType);
            if (gap) gaps.push(gap);
        });
    });
    return gaps;
}

/** An asset that a recommended strategy actually sells or draws down. */
export interface RealisedAssetRef {
    assetType: string;
    assetId: unknown;
}

/**
 * CGT data gaps on the assets a recommendation actually REALISES — the subset the SOA has to disclose,
 * because for those an assumed input has fed a figure the client will act on.
 */
export function findRealisedCgtDataGaps(
    assets: FactFindAssetMap | null | undefined,
    realised: RealisedAssetRef[] | null | undefined,
): CgtDataGap[] {
    if (!assets || !realised?.length) return [];
    const gaps: CgtDataGap[] = [];
    const seen = new Set<string>();
    realised.forEach(({ assetType, assetId }) => {
        const key = `${assetType}::${String(assetId)}`;
        if (seen.has(key)) return; // one disclosure per asset, even if drawn on more than once
        seen.add(key);
        const asset = (assets[assetType] || []).find((a) => String(a.id) === String(assetId));
        const gap = getAssetCgtDataGap(asset, assetType);
        if (gap) gaps.push(gap);
    });
    return gaps;
}

/** How an asset is named in client-facing output. */
function gapDisplayName(gap: CgtDataGap): string {
    if (gap.description) return gap.description;
    return gap.assetType === 'investmentProperty' ? 'your investment property' : 'your shares / managed funds';
}

/**
 * Statement-of-Advice disclosure for assets sold under the recommendation whose CGT rested on assumed
 * inputs, or null when nothing was assumed. Names the assets, states each assumption AND the direction it
 * pushes the tax, so the reader can see which way the estimate could be wrong.
 */
export function buildCgtAssumptionDisclosure(gaps: CgtDataGap[] | null | undefined): string | null {
    if (!gaps?.length) return null;
    const names = gaps.map(gapDisplayName);
    const clauses: string[] = [];
    if (gaps.some((g) => g.missingCostBase)) {
        clauses.push(
            'where no original purchase price was recorded, a cost base of 25% of current value has been assumed, which generally overstates the gain and the tax',
        );
    }
    if (gaps.some((g) => g.missingAcquiredOn)) {
        clauses.push(
            'where no acquisition date was recorded, the asset is assumed to have been owned for more than 12 months (so the 50% CGT discount has been applied) and to have been acquired after 20 September 1985 (so it is not treated as CGT-exempt) — if it was in fact bought within 12 months of the sale, the actual tax would be HIGHER than modelled',
        );
    }
    const assetList = names.length === 1 ? names[0] : `${names.slice(0, -1).join(', ')} and ${names[names.length - 1]}`;
    return (
        `Capital gains tax on the modelled sale of ${assetList} rests on assumed inputs because the records were incomplete: ${clauses.join('; ')}. ` +
        'Confirm the actual cost base and acquisition dates with a registered tax adviser before implementing, as they change the net proceeds available.'
    );
}

/**
 * What is missing and what we assume instead, for display next to the asset.
 *
 * Names the fields as they are labelled on screen ("Cost Base", "Month Acquired") so the reader knows
 * exactly where to go — each of those labels carries its own tooltip explaining the term, so the prompt
 * itself stays short rather than re-teaching it.
 */
export function describeCgtDataGap(gap: CgtDataGap): string {
    const fields: string[] = [];
    const assumptions: string[] = [];
    if (gap.missingCostBase) {
        fields.push('Cost Base');
        assumptions.push('a purchase price of 25% of the current value');
    }
    if (gap.missingAcquiredOn) {
        fields.push('Month/Year Acquired'); // must match the on-screen label exactly — it points there
        assumptions.push('more than 12 months of ownership');
    }
    return `Add ${fields.join(' and ')} above for a more accurate capital gains tax estimate. For now we assume ${assumptions.join(' and ')}.`;
}
